import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { CircleCheck, AlertTriangle, Info } from "lucide-react";
import React from "react";
import { toast } from "@shared/ui/use-toast";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";
import { useSignOffReconciliation } from "../../../../hooks";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSignOff?: () => void;
  data: any;
}

export const SignOffModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSignOff,
  data,
}) => {
  const { id } = useParams<{ id: string }>();
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();
  const signOffMutation = useSignOffReconciliation(id || "", remoteUtilityId || "");

  const currentUserName = React.useMemo(() => {
    try {
      const stored = localStorage.getItem("cached_user_profile");
      return stored ? JSON.parse(stored)?.user?.name || "" : "";
    } catch {
      return "";
    }
  }, []);

  const formFields: FormField[] = [
    {
      name: "managerSignOff",
      type: "text",
      label: "Manager Sign-off",
      required: true,
      placeholder: "Select manager",
      defaultValue: currentUserName,
      disabled: true,
      className: "col-span-1",
    },
    {
      name: "signOffMethod",
      type: "select",
      label: "Sign-off Method",
      required: true,
      placeholder: "Digital signature",
      options: [
        { label: "Digital signature", value: 0 },
        { label: "Physical signature", value: 1 },
      ],
      className: "col-span-1",
    },
    {
      name: "remarks",
      type: "textarea",
      label: "Remarks",
      required: false,
      placeholder: "Optional notes for the audit trail",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
  });

  React.useEffect(() => {
    if (isOpen) {
      form.setValue("managerSignOff", currentUserName);
    }
  }, [isOpen, currentUserName, form]);

  const handleSubmit = (formData: any) => {
    const payload = {
      manager_name: formData.managerSignOff,
      sign_off_method: Number(formData.signOffMethod),
      remarks: formData.remarks || "",
    };

    signOffMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          title: "Day Locked & Signed Off",
          description: "The day-end reconciliation has been archived successfully.",
        });
        if (onSignOff) onSignOff();
        onClose();
      },
    });
  };

  const valueLabelsData = [
    {
      label: "Business Date",
      value: (
        <span className="font-bold text-gray-900">
          {data?.periodFrom} - {data?.periodTo}
        </span>
      ),
    },
    {
      label: "Branch",
      value: (
        <span className="font-bold text-gray-900">
          {data?.sourceBankDisplay}
        </span>
      ),
    },
    {
      label: "System Receipts",
      value: (
        <span className="font-bold text-gray-900">
          {currency} {data?.systemTotal}
        </span>
      ),
    },
    {
      label: "Bank Credits",
      value: (
        <span className="font-bold text-gray-900">
          {currency} {data?.bankTotal}
        </span>
      ),
    },
    {
      label: "Net Variance",
      value: (
        <span className="font-bold text-red-600">
          {currency} {data?.variance}
        </span>
      ),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl text-gray-900">
            <CircleCheck className="w-5 h-5 text-gray-700" />
            Sign Off & Lock Day
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Warning Alert */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-3 text-sm text-amber-800">
            <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
            <p>
              <span className="font-bold">
                This day has open exceptions or variance.
              </span>{" "}
              Signing off will archive it with those items noted — they cannot
              be edited after lock without an admin override.
            </p>
          </div>

          {/* Info Alert */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-3 text-sm text-blue-800">
            <Info className="w-5 h-5 shrink-0 text-blue-600" />
            <p>
              Signing off locks the day — no backdated edits are allowed without
              an admin override. A timestamped report is archived to the audit
              store.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 grid grid-cols-2 gap-4 text-sm mt-4">
            {valueLabelsData.map((item, index) => (
              <ValueLabel key={index} label={item.label} value={item.value} />
            ))}
          </div>

          {/* Form */}
          <div className="mt-4">
            <DynamicForm
              form={form}
              fields={formFields}
              onSubmit={handleSubmit}
              gridColumns={2}
              responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
              onReset={onClose}
              config={{
                layout: "vertical",
                showSubmitButton: true,
                submitButtonText: signOffMutation.isPending ? "Signing Off..." : "Sign Off & Lock",
                showProgress: signOffMutation.isPending,
                icon: CircleCheck,
                showResetButton: true,
                resetButtonText: "Cancel",
                classes: BUTTON_FIELD_CLASSES,
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
